import { Controller } from "@hotwired/stimulus"
import { ArcadeStage } from "../utils/arcade_stage"

// « Qui suis-je ? » : l'indice est fabriqué à partir de la bonne réponse, les trois autres
// propositions sont des sociétés du même type (cf. Arcade::Quiz). Une erreur arrête la
// série — c'est ce qui fait qu'on rejoue.
export default class extends Controller {
  static values = { labels: Object, url: String, scoresUrl: String }

  start() {
    this.stage = new ArcadeStage({
      labels: this.labelsValue,
      scoresUrl: this.scoresUrlValue,
      onRestart: () => this.#play(),
      onClose: () => this.#stop(),
    })
    this.stage.open()
    this.panel = document.createElement("div")
    this.panel.className = "arcade-quiz"
    this.stage.board.appendChild(this.panel)
    this.#play()
  }

  #stop() {
    this.request?.abort()
    this.request = null
    this.panel = null
  }

  #play() {
    this.stage.setScore(0)
    this.#next()
  }

  async #next() {
    this.#renderMessage(this.labelsValue.loading || "…")

    // Une manche annule la précédente : sans ça, un double clic sur « suivante » pouvait
    // faire arriver deux questions et écraser celle qu'on était en train de lire.
    this.request?.abort()
    this.request = new AbortController()

    // Le `try` ne couvre QUE la requête : une erreur de rendu affichée comme une panne
    // réseau enverrait chercher un bug là où il n'est pas.
    let question
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        signal: this.request.signal,
      })
      if (!response.ok) throw new Error(response.statusText)

      question = (await response.json()).question
    } catch (error) {
      if (error.name === "AbortError") return
      this.#renderMessage(this.labelsValue.error || "")
      return
    }

    if (!question) return this.#renderMessage(this.labelsValue.empty || this.labelsValue.error)

    this.question = question
    this.#renderQuestion()
  }

  #renderMessage(text) {
    if (!this.panel) return
    this.panel.innerHTML = ""
    const message = document.createElement("p")
    message.className = "arcade-quiz__message"
    message.textContent = text
    this.panel.appendChild(message)
  }

  #renderQuestion() {
    if (!this.panel) return

    this.panel.innerHTML = ""

    const riddle = document.createElement("p")
    riddle.className = "arcade-quiz__riddle"
    riddle.textContent = this.#riddleText()
    this.panel.appendChild(riddle)

    const list = document.createElement("div")
    list.className = "arcade-quiz__choices"
    this.question.choices.forEach((choice) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "arcade-quiz__choice"
      button.textContent = choice.title
      button.addEventListener("click", () => this.#answer(button, choice), { once: true })
      list.appendChild(button)
    })
    this.panel.appendChild(list)
  }

  // Interpolation à la main du `%{…}` d'I18n : les libellés viennent du serveur, il n'y a pas
  // de moteur de traduction côté client.
  #riddleText() {
    const { kind_label: kind, contracts_label: contracts, teammate } = this.question.clue
    return (this.labelsValue.riddle || "")
      .replace("%{kind}", kind)
      .replace("%{contracts}", contracts)
      .replace("%{teammate}", teammate)
  }

  #answer(button, choice) {
    const correct = choice.id === this.question.answer_id
    const buttons = Array.from(this.panel.querySelectorAll(".arcade-quiz__choice"))
    buttons.forEach((node) => { node.disabled = true })

    const right = buttons[this.question.choices.findIndex((c) => c.id === this.question.answer_id)]
    right?.classList.add("arcade-quiz__choice--right")
    if (!correct) button.classList.add("arcade-quiz__choice--wrong")

    if (!correct) {
      const title = this.question.choices.find((c) => c.id === this.question.answer_id)?.title || ""
      this.stage.finish({
        score: this.stage.score,
        message: (this.labelsValue.wrong || "").replace("%{title}", title),
      })
      return
    }

    this.stage.setScore(this.stage.score + 1)
    this.#appendNextButton()
  }

  #appendNextButton() {
    const next = document.createElement("button")
    next.type = "button"
    next.className = "btn btn-primary arcade-quiz__next"
    next.textContent = this.labelsValue.next || ""
    next.addEventListener("click", () => this.#next(), { once: true })
    this.panel.appendChild(next)
    next.focus()
  }
}
